import { Locale } from '../locale';

/**
 * Represents the role of the user.
 */
export const enum UserRole {
  NORMAL = 0,
  ADMIN = 1,
  SUPER_ADMIN = 2,
  TESTER = 3,
  BEGINNER = 4,
}

/**
 * Represents the status of the Ec2.
 */
export const enum Ec2Status {
  NOT_CREATE = 'NOT_CREATE',
  CREATED = 'CREATED',
  CREATING = 'CREATING',
  ERROR = 'ERROR',
}

export type IPersonalInformation = {
  firstName: string;
  lastName: string;
  addrCity: string;
  addrLine1: string;
  addrLine2?: string;
  addrPostCode: string;
  addrCountry: string;
  addrState: string;
  prefName: string;
  phoneCc: string;
  phoneNo: string;
  role?: string;
};
/**
 * Represents the metadata related to user.
 */
export interface UserMetaData {
  /** Indicates whether the user has filled out the survey. */
  hasFilledSurvey?: boolean;

  /** Stores the user's answers to the survey questions. */
  surveyAnswers?: Record<string, any>;

  /** New user information. */
  adviceForNewbie?: {
    /** Number of times the user turned off the guide.
     * - max: 5 */
    closedAdviceTime?: number;
  };

  personalInformation?: IPersonalInformation;

  openAiImageGenerationTOSAccepted?: boolean;
}

/**
 * Represents the two authenticator of the user.
 */
export interface UserTwoFactorAuthenticationData {
  /** True if the user enables the two factor authentication. */
  isEnabled?: boolean;

  /** The secret of the two authenticator. */
  secret?: string;

  /** The code of the two authenticator to verify setting. */
  code?: number;

  /** The expired time of the code to verify setting. */
  expirationTime?: Date;
}

/**
 * Represents Click's user.
 */
export interface User {
  /** The user's id. */
  id: number;

  /** The user's active status. */
  active: boolean;

  /** The user's ec2 status.
   * - maxLength: 50 */
  ec2Status: Ec2Status;

  /** The user's email
   * - maxLength: 100 */
  email: string;

  /** The user's dev mode status. */
  isDevMode: number;

  /** The user's locale. */
  locale: Locale;

  /** The user's nickName.
   * - maxLength: 500 */
  name: string;

  /** The user's password
   * - maxLength: 500 */
  password: string;

  /** The user's plan Id, use in V1 & user-plan feature */
  planId: string;

  /** The user's role */
  role: UserRole;

  /** The user's beginner status. */
  beginner?: boolean;

  /** The user's connect id in the stripe.
   * - maxLength: 500 */
  connectId?: string | null;

  /** The user's customer id in the stripe.
   * - maxLength: 500 */
  customerId?: string | null;

  /** The user's ec2 endpoint, use in V1.
   * - maxLength: 500*/
  ec2Endpoint?: string | null;

  /** The user's expert status. */
  expert?: boolean | null;

  /** True if this is the first time user logged in. */
  isFirstLogin?: boolean | null;

  /** The user's metadata. */
  metadata?: UserMetaData | null;

  /**
   * company name when signup
   */
  companyName?: string | null;

  /** The user's profile image url.
   * - maxLength: 500 */
  profileImage?: string | null;

  /** The user's two authenticator. */
  twoAuthenticator?: UserTwoFactorAuthenticationData | null;

  /** User id provided by the click user whereas the other id is generated by click
   * - maxLength: 30 */
  userId?: string | null;

  /** The user's verifyToken.
   * - maxLength: 500 */
  verifyToken?: string | null;

  /** The user's deleted at.
   * - Format: date-time */
  deletedAt?: string | null;

  /** The user's account expires at.
   * - Format: date-time */
  accountExpiresAt?: string | null;

  /** The user's created at.
   * - Format: date-time */
  createdAt: string;

  /** The user's updated at.
   * - Format: date-time */
  updatedAt: string;
}
